import { useNode } from '@craftjs/core';
import { alpha, Box, SxProps, Theme, useTheme } from '@mui/material';
import { ReactNode } from 'react';

import { COLORS } from '@/styles/tokens/colors';

import { useIsBlockEditorEditMode } from '../BlockEditor.context';
import { createAllowedTypesRule } from '../BlockEditor.utils';
import { useIsDragging } from '../hooks/useIsDragging';

export interface BlockEditorCanvasContainerProps {
  children?: ReactNode;
  sx?: SxProps<Theme>;
}

export function BlockEditorCanvasContainer({
  children,
  sx,
}: BlockEditorCanvasContainerProps) {
  const theme = useTheme();
  const {
    connectors: { connect },
    hasChildren,
  } = useNode((node) => ({
    hasChildren: node.data.nodes.length > 0,
  }));
  const isEditMode = useIsBlockEditorEditMode();
  const isDragging = useIsDragging();

  // Only show minHeight for empty canvases (to give a visible drop target)
  const minHeight = isEditMode && !hasChildren ? 100 : 'unset';

  // Show dashed outline when a node is being dragged to indicate drop zones
  const showDropZoneOutline = isEditMode && isDragging;

  return (
    <Box
      ref={(ref: HTMLDivElement | null) => {
        if (ref && isEditMode) connect(ref);
      }}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        gap: 2,
        minHeight,
        borderRadius: '4px',
        outline: showDropZoneOutline
          ? `1px dashed ${COLORS.GRAY[300]}`
          : '1px dashed transparent',
        backgroundColor: showDropZoneOutline
          ? alpha(COLORS.BLUE[50], 0.4)
          : 'transparent',
        transition: `outline-color ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut}, background-color ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut}`,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

BlockEditorCanvasContainer.craft = {
  displayName: 'Block Editor Canvas',
  props: {},
  rules: {
    canMoveIn: createAllowedTypesRule(),
  },
};
